import {
  Modal,
  Textarea,
  Group,
  Button,
  Input,
  ColorSwatch,
  Checkbox,
  Text,
  Flex,
  Box,
} from "@mantine/core";
import { DatePickerInput, DatesProvider } from "@mantine/dates";
import { useEffect, useState } from "react";
import { IconCalendar } from "../../../shared/svg/IconCalendar";
import { useTranslation } from "react-i18next";
import useProfileStore from "../../../store/useProfileStore";
import { CheckIconTask } from "../../../shared/svg/CheckIcon";
import useDevelopmentStore from "../../../store/useDevelopmentStore";
import dayjs from "dayjs";
import { TrashCan } from "../../../shared/svg/TrashCan";
import { useMediaQuery } from "@mantine/hooks";
import ConfirmDelete from "../../../utils/components/ConfirmDelete";

export type TaskFormValues = {
  id?: number;
  title: string;
  description: string;
  completed: boolean;
  day_from: string;
  day_to: string;
  display_color: string;
  reminder_me: boolean;
};

const colorMap = {
  purple: "rgba(225, 215, 235)",
  red: "rgba(255, 215, 215)",
  yellow: "rgba(255, 248, 215)",
  green: "rgba(215, 255, 229)",
  blue: "rgba(215, 229, 255)",
  pink: "rgba(255, 215, 235)",
  orange: "rgba(255, 230, 215)",
  grey: "rgba(235, 235, 235)",
};

type EditProps = {
  opened: boolean;
  onClose: () => void;
  goalId: number | null;
  taskToEdit: TaskFormValues | null;
};

export function CalendarEditTimestamp({
  opened,
  onClose,
  goalId,
  taskToEdit,
}: EditProps) {
  const { t } = useTranslation();
  const { profileLanguage } = useProfileStore();
  const { editTask, currentPlanId, deleteTask, fetchLearningPlanGoals } =
    useDevelopmentStore();
  const [formErrors, setFormErrors] = useState<{
    title?: string;
    dateRange?: string;
  }>({});
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    completed: false,
    reminder_me: false,
    dateRange: [null, null] as [Date | null, Date | null],
    display_color: "purple",
  });

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const matches = useMediaQuery("(max-width: 650px)");

  useEffect(() => {
    if (taskToEdit) {
      setFormData({
        title: taskToEdit.title,
        description: taskToEdit.description,
        completed: taskToEdit.completed,
        reminder_me: taskToEdit.reminder_me,

        dateRange: [new Date(taskToEdit.day_from), new Date(taskToEdit.day_to)],
        display_color: taskToEdit.display_color,
      });
    }
  }, [taskToEdit]);

  const handleSubmit = async () => {
    setFormErrors({});

    const [start, end] = formData.dateRange;
    const errors: { title?: string; dateRange?: string } = {};

    if (!formData.title.trim()) {
      errors.title = t("field_required");
    }

    if (!start || !end) {
      errors.dateRange = t("field_required");
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const taskData: TaskFormValues = {
      title: formData.title,
      description: formData.description,
      completed: formData.completed,
      reminder_me: formData.reminder_me,
      day_from: dayjs(start).format("YYYY-MM-DD"),
      day_to: dayjs(end).format("YYYY-MM-DD"),
      display_color: formData.display_color,
    };

    try {
      if (currentPlanId && goalId) {
        await editTask({
          planId: currentPlanId,
          goalId,
          taskId: taskToEdit!.id!,
          credentials: taskData,
        });
        fetchLearningPlanGoals(currentPlanId!);

        onClose();
      }
    } catch (err: any) {
      setFormErrors(
        Object.keys(err.response.data.errors).reduce((acc, key) => {
          acc[key] = err.response.data.errors[key][0];
          return acc;
        }, {} as { [key: string]: string })
      );
    }
  };

  const handleCancel = () => {
    onClose();
    setFormErrors({});
  };

  const handleDelete = async () => {
    try {
      if (goalId && taskToEdit?.id && currentPlanId) {
        deleteTask({
          taskId: taskToEdit.id,
          goalId,
          planId: currentPlanId,
        });

        onClose();
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Modal
      zIndex={301}
      opened={opened}
      onClose={handleCancel}
      title={<Text fw={600}>{t("form.editTask")}</Text>}
      centered
    >
      <Input.Wrapper
        label={t("form.title")}
        withAsterisk
        mt="sm"
        error={formErrors.title}
      >
        <Input
          placeholder={t("form.title")}
          value={formData.title}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setFormData((prev) => ({ ...prev, title: value }));

            if (formErrors.title) {
              setFormErrors((prev) => {
                const { title, ...rest } = prev;
                return rest;
              });
            }
          }}
          required
        />
      </Input.Wrapper>

      <Textarea
        label={t("form.description")}
        value={formData.description}
        onChange={(e) => {
          const value = e.currentTarget.value;
          setFormData((prev) => ({
            ...prev,
            description: value,
          }));
        }}
        autosize
        minRows={1}
        mt="md"
      />

      <Checkbox
        label={t("form.completed")}
        checked={formData.completed}
        onChange={(event) => {
          const value = event.currentTarget.checked;
          setFormData((prev) => ({
            ...prev,
            completed: value,
          }));
        }}
        mt="md"
        size="md"
      />

      <DatesProvider settings={{ locale: Object.keys(profileLanguage!)[0] }}>
        <DatePickerInput
          type="range"
          popoverProps={{
            zIndex: 302,
          }}
          allowSingleDateInRange
          label={`${t("form.startDate")} / ${t("form.endDate")}`}
          value={formData.dateRange}
          onChange={(range) => {
            setFormData((prev) => ({ ...prev, dateRange: range }));
            if (formErrors.dateRange) {
              setFormErrors((prev) => {
                const { dateRange, ...rest } = prev;
                return rest;
              });
            }
          }}
          placeholder={t("pick_daterange")}
          radius="md"
          size="md"
          variant="default"
          clearable
          leftSection={<IconCalendar />}
          mt="md"
          error={formErrors.dateRange}
          renderDay={(date) => {
            const isToday = dayjs(date).isSame(new Date(), "day");

            return (
              <Box
                style={{
                  backgroundColor: isToday ? "#faf0fd" : "transparent",
                  color: isToday ? "#862e9c" : "inherit",
                  border: isToday ? "1px solid #862e9c" : "none",
                  borderRadius: 6,
                  width: 34,
                  height: 34,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: isToday ? 700 : 400,
                }}
              >
                {date.getDate()}
              </Box>
            );
          }}
        />
      </DatesProvider>

      <Checkbox
        label={t("form.reminder")}
        checked={formData.reminder_me}
        onChange={(event) => {
          const value = event.currentTarget.checked;
          setFormData((prev) => ({
            ...prev,
            reminder_me: value,
          }));
        }}
        mt="xs"
        ml="xs"
      />
      <Text fw={600} mb={12} mt={22}>
        {t("pick_color")}
      </Text>
      <Group mt="lg" justify="center">
        {Object.entries(colorMap).map(([key, value]) => (
          <ColorSwatch
            key={key}
            color={value}
            size={matches ? 22 : 25}
            component="button"
            onClick={() => {
              setFormData((prev) => ({ ...prev, display_color: key }));
            }}
            style={{
              cursor: "pointer",
              border:
                formData.display_color === key ? `2px solid ${value}` : "none",
              position: "relative",
            }}
          >
            {formData.display_color === key && <CheckIconTask />}
          </ColorSwatch>
        ))}
      </Group>

      <ConfirmDelete
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onDelete={handleDelete}
        title={t("delete_task_title")}
      />

      <Flex align="center" justify="space-between" mt="40px">
        <Button
          size="compact-md"
          variant="subtle"
          onClick={() => setIsDeleteOpen(true)}
        >
          <TrashCan />
        </Button>
        <Group justify="flex-end">
          <Button size="sm" variant="default" onClick={handleCancel}>
            {t("cancel")}
          </Button>
          <Button size="sm" onClick={handleSubmit}>
            {t("save")}
          </Button>
        </Group>
      </Flex>
    </Modal>
  );
}
